/**
 * LoadingState wrapper component.
 *
 * ADR-022 Loading State Pattern: renders loading fallback, error fallback,
 * or children based on the current state.
 *
 * Usage:
 *   <LoadingState loading={isLoading} error={error}>
 *     <DataView data={data} />
 *   </LoadingState>
 */
import type { ReactNode } from 'react';

export interface LoadingStateProps {
  loading: boolean;
  error?: Error | string | null;
  children: ReactNode;
  loadingFallback?: ReactNode;
  errorFallback?: ReactNode | ((error: Error | string) => ReactNode);
}

export function LoadingState({
  loading,
  error,
  children,
  loadingFallback,
  errorFallback,
}: LoadingStateProps) {
  if (loading) {
    return (
      <>
        {loadingFallback ?? (
          <div className="loading-state" role="status" aria-label="Loading">
            <div className="loading-state__spinner" />
            <p className="loading-state__text">Loading...</p>
          </div>
        )}
      </>
    );
  }

  if (error) {
    const errorMessage = typeof error === 'string' ? error : error.message;

    if (typeof errorFallback === 'function') {
      return <>{errorFallback(error)}</>;
    }

    return (
      <>
        {errorFallback ?? (
          <div className="loading-state loading-state--error" role="alert">
            <p className="loading-state__error-text">{errorMessage}</p>
          </div>
        )}
      </>
    );
  }

  return <>{children}</>;
}
